import { useEffect, useState } from 'react'

// Define breakpoint keys as a const array for iteration
const BREAKPOINT_KEYS = [
  'mobile',
  'tablet',
  'desktopSmall',
  'desktopMedium',
  'desktopLarge',
] as const

// Derive the Breakpoint type from the array
type Breakpoint = (typeof BREAKPOINT_KEYS)[number]

interface BreakpointState {
  isMobile: boolean
  isTablet: boolean
  isDesktopSmall: boolean
  isDesktopMedium: boolean
  isDesktopLarge: boolean
  current: Breakpoint
}

// Define breakpoints as a const object with explicit typing
const BREAKPOINT_QUERIES = {
  mobile: null,
  tablet: '(width > 640px)',
  desktopSmall: '(width > 1080px)',
  desktopMedium: '(width > 1280px)',
  desktopLarge: '(width > 1440px)',
} as const satisfies Record<Breakpoint, string | null>

type BreakpointQueries = {
  [K in Breakpoint]: K extends 'mobile' ? null : MediaQueryList
}

/**
 * Creates MediaQueryList objects for each breakpoint.
 * Mobile is null because it's our default/fallback in the mobile-first approach.
 *
 * Breakpoints:
 * ≥ 1440 = desktop large
 * 1281–1439 = desktop medium
 * 1081–1280 = desktop small
 * 641–1080 = tablet
 * ≤ 640px = mobile (default)
 */
const createBreakpoints = (): BreakpointQueries => {
  const result = {
    mobile: null,
    tablet: window.matchMedia(BREAKPOINT_QUERIES.tablet),
    desktopSmall: window.matchMedia(BREAKPOINT_QUERIES.desktopSmall),
    desktopMedium: window.matchMedia(BREAKPOINT_QUERIES.desktopMedium),
    desktopLarge: window.matchMedia(BREAKPOINT_QUERIES.desktopLarge),
  }

  return result
}

/**
 * Determines the current breakpoint by checking which media queries match.
 * Uses mobile-first logic: iterates through breakpoints and returns the last matching one.
 * Since larger breakpoints also match smaller ones (e.g., 1500px matches both tablet AND desktopLarge),
 * the iteration order ensures we get the largest matching breakpoint.
 */
function getCurrentBreakpoint(breakpoints: BreakpointQueries): Breakpoint {
  let bp: Breakpoint = 'mobile'

  // Iterate through all breakpoints in order
  for (const key of BREAKPOINT_KEYS) {
    const mq = breakpoints[key]
    // If no media query (mobile) or media query matches, update bp
    // This ensures the last (largest) matching breakpoint wins
    if (mq === null || mq.matches) {
      bp = key
    }
  }

  return bp
}

/**
 * Hook that tracks the current viewport breakpoint and provides boolean flags for each size.
 *
 * @returns Object containing:
 *  - Boolean flags for each breakpoint (isMobile, isTablet, etc.)
 *  - current: The active breakpoint as a string
 *
 * @example
 * const { isMobile, isDesktopLarge } = useBreakpoint()
 * return isMobile ? <MobileMenu /> : <DesktopMenu />
 */
export function useBreakpoint(): BreakpointState {
  // Initialize state with current breakpoint
  // Lazy initialization prevents unnecessary calls during SSR
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    // SSR safety: return mobile as default when window is undefined
    if (typeof window === 'undefined') return 'mobile'
    return getCurrentBreakpoint(createBreakpoints())
  })

  useEffect(() => {
    // Create MediaQueryList objects once for this effect lifecycle
    const breakpoints = createBreakpoints()

    /**
     * Handler called whenever any media query changes.
     * Recalculates the current breakpoint and updates state.
     */
    function mqHandler() {
      setBreakpoint(getCurrentBreakpoint(breakpoints))
    }

    // Attach change listeners to all media queries
    for (const key of BREAKPOINT_KEYS) {
      const mq = breakpoints[key]
      if (mq) {
        mq.addEventListener('change', mqHandler)
      }
    }

    // Check immediately on mount to ensure state is correct
    // (in case viewport changed between useState initialization and effect running)
    mqHandler()

    // Cleanup: remove all event listeners on unmount
    return () => {
      for (const key of BREAKPOINT_KEYS) {
        const mq = breakpoints[key]
        if (mq) {
          mq.removeEventListener('change', mqHandler)
        }
      }
    }
  }, []) // Empty dependency array: only run once on mount

  // Return object with boolean flags for each breakpoint plus the current string
  return {
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktopSmall: breakpoint === 'desktopSmall',
    isDesktopMedium: breakpoint === 'desktopMedium',
    isDesktopLarge: breakpoint === 'desktopLarge',
    current: breakpoint,
  }
}
